import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiArrowRight, FiInfo } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms of Service | ManagedSite.net";
    
    const description = "Read the ManagedSite.net terms of service covering website use, agency service inquiries and general conditions for using this site.";
    const title = "Terms of Service | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  return (
    <div className="w-full bg-white">
      {/* Hero Section */}
      <section className="bg-[#FAF8F3] pt-24 pb-16 lg:pt-36 lg:pb-20 border-b border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-6">Legal</div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-[#17211E] mb-8">Terms of Service</h1>
          <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium max-w-2xl mx-auto">
            These terms explain the conditions for using the ManagedSite.net website and submitting service inquiries.
          </p>
          <div className="mt-8 text-sm font-bold text-[#17211E]/40 uppercase tracking-widest">
            Last updated: August 6, 2026
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#EAF1EC]/50 border border-[#3B7C6D]/10 p-8 rounded-2xl mb-16 flex items-start gap-4">
            <div className="text-[#174C3C] mt-1">
              <SafeIcon icon={FiInfo} className="w-6 h-6" />
            </div>
            <p className="text-lg font-bold text-[#174C3C]">
              These terms provide general website terms. Formal service agreements, statements of work or plan terms may apply separately to paid services. This page is not legal advice.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-16">
            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">1. Agreement to Terms</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>By using this website, you agree to these terms. If you do not agree, do not use the site.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">2. Who We Are</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>ManagedSite.net is a service of MyGoto Brands, Inc. MyGoto is a related brand of the same company.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">3. Website Purpose</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>This website provides information about ManagedSite.net services and allows visitors to submit inquiries. Website content is for general informational purposes and may change without notice.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">4. No Guarantee from Website Content</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>Service descriptions, pricing guidance and examples on this website do not automatically create a service contract. Paid services are provided under accepted plans, proposals, statements of work or other agreed terms. Hosting pricing guidance, where shown, may be directional and subject to confirmation.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">5. Agency Relationship</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>When ManagedSite.net works with an agency, the agency generally owns the client relationship unless otherwise agreed in writing. ManagedSite.net does not solicit an agency’s clients.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">6. Inquiries and Support Requests</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>Submitting a form or email does not guarantee acceptance of work, emergency response or immediate support. Emergency or after-hours availability is not guaranteed. Response times can vary based on request volume, complexity and service level.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">7. Acceptable Use</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>You agree not to misuse the website, attempt unauthorized access, submit malicious content, use the site for unlawful purposes, or interfere with site operations or security.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">8. Intellectual Property</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>Website content, branding, design and materials are owned by MyGoto Brands, Inc. or used with permission. You may not copy or reuse site materials for commercial purposes without permission.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">9. Third-Party Services and Links</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>The website may reference or link to third-party tools, platforms or websites. We are not responsible for third-party sites, tools or services we do not control.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">10. Service Limitations</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>Website management, support, hosting, email and project services have scoped inclusions and exclusions. Monitoring and maintenance reduce risk but do not guarantee that all issues will be prevented. Unused included support time does not roll over unless an agreement says otherwise.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">11. Disclaimer</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>The website and its content are provided on an as-available basis. To the fullest extent permitted by law, MyGoto Brands, Inc. disclaims warranties regarding uninterrupted availability, error-free content or fitness for a particular purpose arising solely from website use.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">12. Limitation of Liability</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>To the fullest extent permitted by law, MyGoto Brands, Inc. is not liable for indirect, incidental, special or consequential damages arising from website use or reliance on website content. Separate limitations may apply in service agreements for paid work.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">13. Indemnity</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>You agree to avoid misusing the website and to be responsible for information you submit through forms or communications.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">14. Changes to Terms</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>We may update these terms from time to time. Continued use of the website after changes means you accept the updated terms.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold text-[#17211E] mb-6">15. Contact</h2>
              <div className="text-[#17211E]/75 font-medium space-y-4">
                <p>Questions about these terms can be submitted through our <Link to="/contact" className="text-[#174C3C] underline font-bold">contact page</Link>.</p>
                <p className="font-bold">ManagedSite.net is a service of MyGoto Brands, Inc.</p>
              </div>
            </section>
          </div>

          <div className="mt-20 pt-10 border-t border-[#EAF1EC] flex flex-col sm:flex-row justify-between items-center gap-8">
            <Link to="/contact" className="home-button">
              Contact Us
            </Link>
            <Link to="/privacy" className="text-[#174C3C] font-bold hover:text-[#3B7C6D] flex items-center gap-2">
              View Privacy Policy <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;